import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/layout/Layout';
import Header from '../components/layout/Header';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { Bell, Mail, AlertTriangle, Clock, Settings2, Send, Play, RefreshCw, CheckCircle, Calendar, Plus, Trash2, Edit2, Save, X } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const FUNCTIONS = [
  { value: 'brokerage', label: 'Brokerage' },
  { value: 'mga', label: 'MGA' },
  { value: 'marketing', label: 'Marketing' },
  { value: 'finance', label: 'Finance' },
  { value: 'people', label: 'People' }
];

const CADENCES = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'yearly', label: 'Yearly' }
];

const DAYS_OF_WEEK = [
  { value: 0, label: 'Monday' },
  { value: 1, label: 'Tuesday' },
  { value: 2, label: 'Wednesday' },
  { value: 3, label: 'Thursday' },
  { value: 4, label: 'Friday' },
  { value: 5, label: 'Saturday' },
  { value: 6, label: 'Sunday' }
];

const NotificationSettings = () => {
  const { isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    notify_on_yellow: true,
    notify_on_red: true,
    admin_cc_on_reminders: true,
    renotify_interval_days: 7,
    variance_worsening_threshold: 0.05,
    default_timezone: 'America/New_York',
    email_enabled: true,
    in_app_enabled: true
  });
  
  const [testData, setTestData] = useState({
    function: 'brokerage',
    status: 'red'
  });

  const [schedulerStatus, setSchedulerStatus] = useState(null);
  const [triggeringScheduler, setTriggeringScheduler] = useState(false);
  
  // Upload schedules state
  const [uploadSchedules, setUploadSchedules] = useState([]);
  const [loadingSchedules, setLoadingSchedules] = useState(true);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [showNewScheduleForm, setShowNewScheduleForm] = useState(false);
  const [newSchedule, setNewSchedule] = useState({
    function: 'brokerage',
    cadence: 'weekly',
    due_day: 0,
    due_hour: 9,
    due_minute: 0,
    timezone: 'America/New_York',
    escalation_enabled: true,
    is_active: true
  });

  useEffect(() => {
    fetchSettings();
    fetchSchedulerStatus();
    fetchUploadSchedules();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/notifications/settings`);
      setSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch settings:', error);
      toast.error('Failed to load notification settings');
    } finally {
      setLoading(false);
    }
  };

  const fetchSchedulerStatus = async () => {
    try {
      const response = await axios.get(`${API}/notifications/scheduler/status`);
      setSchedulerStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch scheduler status:', error);
    }
  };

  const fetchUploadSchedules = async () => {
    setLoadingSchedules(true);
    try {
      const response = await axios.get(`${API}/notifications/upload-schedules`);
      setUploadSchedules(response.data);
    } catch (error) {
      console.error('Failed to fetch upload schedules:', error);
    } finally {
      setLoadingSchedules(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/notifications/settings`, settings);
      toast.success('Settings saved successfully');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const createUploadSchedule = async () => {
    try {
      await axios.post(`${API}/notifications/upload-schedules`, newSchedule);
      toast.success('Upload schedule created');
      setShowNewScheduleForm(false);
      setNewSchedule({
        function: 'brokerage',
        cadence: 'weekly',
        due_day: 0,
        due_hour: 9,
        due_minute: 0,
        timezone: 'America/New_York',
        escalation_enabled: true,
        is_active: true
      });
      fetchUploadSchedules();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create schedule');
    }
  };

  const updateUploadSchedule = async (scheduleId, updates) => {
    try {
      await axios.put(`${API}/notifications/upload-schedules/${scheduleId}`, updates);
      toast.success('Schedule updated');
      setEditingSchedule(null);
      fetchUploadSchedules();
    } catch (error) {
      toast.error('Failed to update schedule');
    }
  };

  const deleteUploadSchedule = async (scheduleId, scheduleName) => {
    const confirmed = window.confirm(`Are you sure you want to delete the ${scheduleName} schedule?`);
    if (!confirmed) return;
    
    try {
      await axios.delete(`${API}/notifications/upload-schedules/${scheduleId}`);
      toast.success('Schedule deleted');
      fetchUploadSchedules();
      // Also refresh scheduler status to show updated jobs
      fetchSchedulerStatus();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to delete schedule');
    }
  };

  const sendTestNotification = async (type) => {
    try {
      const endpoint = type === 'kpi' 
        ? `${API}/notifications/test/kpi-missed`
        : `${API}/notifications/test/upload-reminder`;
      
      const data = type === 'kpi' 
        ? {
            kpi_name: "Test KPI",
            function: testData.function,
            period_label: "January 2025",
            actual_value: 900000,
            plan_value: 1000000,
            variance: -0.10,
            status: testData.status,
            unit: "$"
          }
        : {
            function: testData.function,
            period_label: "January 2025",
            due_date: "2025-01-03 09:00 AM",
            escalation_level: 1
          };
      
      const response = await axios.post(endpoint, data);
      toast.success(response.data.message);
    } catch (error) {
      toast.error('Failed to send test notification');
    }
  };

  const triggerSchedulerCheck = async () => {
    setTriggeringScheduler(true);
    try {
      const response = await axios.post(`${API}/notifications/scheduler/trigger`, {});
      toast.success('Scheduler check triggered');
      setTimeout(fetchSchedulerStatus, 2000);
    } catch (error) {
      toast.error('Failed to trigger scheduler');
    } finally {
      setTriggeringScheduler(false);
    }
  };

  const formatTime = (hour, minute) => {
    const h = hour % 12 || 12;
    const ampm = hour < 12 ? 'AM' : 'PM';
    return `${h}:${minute.toString().padStart(2, '0')} ${ampm}`;
  };

  const getDueDayLabel = (cadence, dueDay) => {
    if (cadence === 'weekly') {
      return DAYS_OF_WEEK.find(d => d.value === dueDay)?.label || `Day ${dueDay}`;
    }
    return `Day ${dueDay}`;
  };

  if (!isAdmin()) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-[60vh]">
          <p className="text-slate-500">Access denied. Admin only.</p>
        </div>
      </Layout>
    );
  }

  if (loading) {
    return (
      <Layout>
        <Header title="Notification Settings" subtitle="Configure alerts and reminders" />
        <div className="p-6 space-y-6">
          <Skeleton className="h-48 w-full" />
          <Skeleton className="h-48 w-full" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <Header title="Notification Settings" subtitle="Configure alerts and reminders" />
      
      <div className="p-4 sm:p-6 space-y-6" data-testid="notification-settings">
        {/* Upload Schedules - NEW SECTION */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-blue-500" />
                  Upload Schedules & Deadlines
                </CardTitle>
                <CardDescription>
                  Configure upload deadlines and reminder times for each department
                </CardDescription>
              </div>
              <Button 
                onClick={() => setShowNewScheduleForm(true)}
                className="bg-[#2563EB]"
                size="sm"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Schedule
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* New Schedule Form */}
            {showNewScheduleForm && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="font-medium text-blue-900">New Upload Schedule</h4>
                  <Button variant="ghost" size="sm" onClick={() => setShowNewScheduleForm(false)}>
                    <X className="w-4 h-4" />
                  </Button>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="space-y-2">
                    <Label>Department</Label>
                    <Select 
                      value={newSchedule.function} 
                      onValueChange={(v) => setNewSchedule({...newSchedule, function: v})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {FUNCTIONS.map(f => (
                          <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Cadence</Label>
                    <Select 
                      value={newSchedule.cadence} 
                      onValueChange={(v) => setNewSchedule({...newSchedule, cadence: v})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {CADENCES.map(c => (
                          <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>{newSchedule.cadence === 'weekly' ? 'Due Day (of week)' : 'Due Day (of month/period)'}</Label>
                    {newSchedule.cadence === 'weekly' ? (
                      <Select 
                        value={newSchedule.due_day.toString()} 
                        onValueChange={(v) => setNewSchedule({...newSchedule, due_day: parseInt(v)})}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {DAYS_OF_WEEK.map(d => (
                            <SelectItem key={d.value} value={d.value.toString()}>{d.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Input 
                        type="number" 
                        min="1" 
                        max="31" 
                        value={newSchedule.due_day}
                        onChange={(e) => setNewSchedule({...newSchedule, due_day: parseInt(e.target.value) || 1})}
                      />
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label>Due Time</Label>
                    <div className="flex gap-2">
                      <Input 
                        type="number" 
                        min="0" 
                        max="23" 
                        value={newSchedule.due_hour}
                        onChange={(e) => setNewSchedule({...newSchedule, due_hour: parseInt(e.target.value) || 0})}
                        className="w-16"
                        placeholder="Hour"
                      />
                      <span className="self-center">:</span>
                      <Input 
                        type="number" 
                        min="0" 
                        max="59" 
                        value={newSchedule.due_minute}
                        onChange={(e) => setNewSchedule({...newSchedule, due_minute: parseInt(e.target.value) || 0})}
                        className="w-16"
                        placeholder="Min"
                      />
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <Switch 
                      checked={newSchedule.escalation_enabled}
                      onCheckedChange={(v) => setNewSchedule({...newSchedule, escalation_enabled: v})}
                    />
                    <Label>Enable Escalation</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch 
                      checked={newSchedule.is_active}
                      onCheckedChange={(v) => setNewSchedule({...newSchedule, is_active: v})}
                    />
                    <Label>Active</Label>
                  </div>
                </div>
                <Button onClick={createUploadSchedule} className="bg-[#2563EB]">
                  <Save className="w-4 h-4 mr-2" />
                  Create Schedule
                </Button>
              </div>
            )}

            {/* Existing Schedules */}
            {loadingSchedules ? (
              <Skeleton className="h-32 w-full" />
            ) : uploadSchedules.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <Calendar className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No upload schedules configured</p>
                <p className="text-sm">Click "Add Schedule" to create your first upload deadline</p>
              </div>
            ) : (
              <div className="space-y-3">
                {uploadSchedules.map((schedule) => (
                  <div 
                    key={schedule.id} 
                    className={`border rounded-lg p-4 ${schedule.is_active ? 'bg-white' : 'bg-slate-50 opacity-75'}`}
                  >
                    {editingSchedule === schedule.id ? (
                      // Edit mode
                      <div className="space-y-4">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          <div className="space-y-2">
                            <Label>Department</Label>
                            <div className="font-medium text-slate-700 py-2">
                              {FUNCTIONS.find(f => f.value === schedule.function)?.label}
                            </div>
                          </div>
                          <div className="space-y-2">
                            <Label>Cadence</Label>
                            <div className="font-medium text-slate-700 py-2">
                              {CADENCES.find(c => c.value === schedule.cadence)?.label}
                            </div>
                          </div>
                          <div className="space-y-2">
                            <Label>{schedule.cadence === 'weekly' ? 'Due Day' : 'Due Day'}</Label>
                            {schedule.cadence === 'weekly' ? (
                              <Select 
                                value={schedule.due_day?.toString() || "0"} 
                                onValueChange={(v) => {
                                  const updated = uploadSchedules.map(s => 
                                    s.id === schedule.id ? {...s, due_day: parseInt(v)} : s
                                  );
                                  setUploadSchedules(updated);
                                }}
                              >
                                <SelectTrigger>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {DAYS_OF_WEEK.map(d => (
                                    <SelectItem key={d.value} value={d.value.toString()}>{d.label}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            ) : (
                              <Input 
                                type="number" 
                                min="1" 
                                max="31" 
                                value={schedule.due_day || 1}
                                onChange={(e) => {
                                  const updated = uploadSchedules.map(s => 
                                    s.id === schedule.id ? {...s, due_day: parseInt(e.target.value) || 1} : s
                                  );
                                  setUploadSchedules(updated);
                                }}
                              />
                            )}
                          </div>
                          <div className="space-y-2">
                            <Label>Due Time</Label>
                            <div className="flex gap-2">
                              <Input 
                                type="number" 
                                min="0" 
                                max="23" 
                                value={schedule.due_hour || 9}
                                onChange={(e) => {
                                  const updated = uploadSchedules.map(s => 
                                    s.id === schedule.id ? {...s, due_hour: parseInt(e.target.value) || 0} : s
                                  );
                                  setUploadSchedules(updated);
                                }}
                                className="w-16"
                              />
                              <span className="self-center">:</span>
                              <Input 
                                type="number" 
                                min="0" 
                                max="59" 
                                value={schedule.due_minute || 0}
                                onChange={(e) => {
                                  const updated = uploadSchedules.map(s => 
                                    s.id === schedule.id ? {...s, due_minute: parseInt(e.target.value) || 0} : s
                                  );
                                  setUploadSchedules(updated);
                                }}
                                className="w-16"
                              />
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="flex items-center gap-2">
                            <Switch 
                              checked={schedule.escalation_enabled}
                              onCheckedChange={(v) => {
                                const updated = uploadSchedules.map(s => 
                                  s.id === schedule.id ? {...s, escalation_enabled: v} : s
                                );
                                setUploadSchedules(updated);
                              }}
                            />
                            <Label>Enable Escalation</Label>
                          </div>
                          <div className="flex items-center gap-2">
                            <Switch 
                              checked={schedule.is_active}
                              onCheckedChange={(v) => {
                                const updated = uploadSchedules.map(s => 
                                  s.id === schedule.id ? {...s, is_active: v} : s
                                );
                                setUploadSchedules(updated);
                              }}
                            />
                            <Label>Active</Label>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button 
                            size="sm"
                            onClick={() => updateUploadSchedule(schedule.id, {
                              due_day: schedule.due_day,
                              due_hour: schedule.due_hour,
                              due_minute: schedule.due_minute,
                              escalation_enabled: schedule.escalation_enabled,
                              is_active: schedule.is_active
                            })}
                            className="bg-[#2563EB]"
                          >
                            <Save className="w-4 h-4 mr-2" />
                            Save
                          </Button>
                          <Button 
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setEditingSchedule(null);
                              fetchUploadSchedules();
                            }}
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    ) : (
                      // View mode
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-6">
                          <div>
                            <div className="font-medium text-slate-900">
                              {FUNCTIONS.find(f => f.value === schedule.function)?.label}
                            </div>
                            <div className="text-sm text-slate-500">
                              {CADENCES.find(c => c.value === schedule.cadence)?.label}
                            </div>
                          </div>
                          <div className="text-sm">
                            <span className="text-slate-500">Due: </span>
                            <span className="font-medium">
                              {getDueDayLabel(schedule.cadence, schedule.due_day)} at {formatTime(schedule.due_hour || 9, schedule.due_minute || 0)}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            {schedule.escalation_enabled && (
                              <span className="text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded">
                                Escalation On
                              </span>
                            )}
                            {!schedule.is_active && (
                              <span className="text-xs bg-slate-200 text-slate-600 px-2 py-1 rounded">
                                Inactive
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => setEditingSchedule(schedule.id)}
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => deleteUploadSchedule(schedule.id, `${FUNCTIONS.find(f => f.value === schedule.function)?.label} ${schedule.cadence}`)}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            data-testid={`delete-schedule-${schedule.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* KPI Alert Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              KPI Missed Alerts
            </CardTitle>
            <CardDescription>
              Configure when to send notifications for underperforming KPIs
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Notify on Yellow Status</Label>
                <p className="text-sm text-slate-500">Send alerts when KPIs are at risk (Yellow)</p>
              </div>
              <Switch
                checked={settings.notify_on_yellow}
                onCheckedChange={(checked) => setSettings({ ...settings, notify_on_yellow: checked })}
              />
            </div>
            
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Notify on Red Status</Label>
                <p className="text-sm text-slate-500">Send alerts when KPIs are missed (Red)</p>
              </div>
              <Switch
                checked={settings.notify_on_red}
                onCheckedChange={(checked) => setSettings({ ...settings, notify_on_red: checked })}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Re-notify Interval (days)</Label>
                <Input
                  type="number"
                  min="1"
                  max="30"
                  value={settings.renotify_interval_days}
                  onChange={(e) => setSettings({ ...settings, renotify_interval_days: parseInt(e.target.value) || 7 })}
                />
                <p className="text-xs text-slate-500">Days before re-sending the same alert</p>
              </div>
              
              <div className="space-y-2">
                <Label>Variance Worsening Threshold (%)</Label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  step="1"
                  value={settings.variance_worsening_threshold * 100}
                  onChange={(e) => setSettings({ ...settings, variance_worsening_threshold: (parseFloat(e.target.value) || 5) / 100 })}
                />
                <p className="text-xs text-slate-500">Re-notify if variance worsens by this amount</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Upload Reminder Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-blue-500" />
              Upload Reminders
            </CardTitle>
            <CardDescription>
              Configure automatic reminders for data uploads
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>CC Admin on Reminders</Label>
                <p className="text-sm text-slate-500">Include admins on all upload reminders</p>
              </div>
              <Switch
                checked={settings.admin_cc_on_reminders}
                onCheckedChange={(checked) => setSettings({ ...settings, admin_cc_on_reminders: checked })}
              />
            </div>

            <div className="space-y-2">
              <Label>Default Timezone</Label>
              <Select 
                value={settings.default_timezone} 
                onValueChange={(value) => setSettings({ ...settings, default_timezone: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="America/New_York">Eastern Time (ET)</SelectItem>
                  <SelectItem value="America/Chicago">Central Time (CT)</SelectItem>
                  <SelectItem value="America/Denver">Mountain Time (MT)</SelectItem>
                  <SelectItem value="America/Los_Angeles">Pacific Time (PT)</SelectItem>
                  <SelectItem value="UTC">UTC</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Notification Channels */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Bell className="w-5 h-5 text-slate-500" />
              Notification Channels
            </CardTitle>
            <CardDescription>
              Choose how notifications are delivered
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5 flex items-center gap-3">
                <Bell className="w-5 h-5 text-slate-400" />
                <div>
                  <Label>In-App Notifications</Label>
                  <p className="text-sm text-slate-500">Show notifications in the app</p>
                </div>
              </div>
              <Switch
                checked={settings.in_app_enabled}
                onCheckedChange={(checked) => setSettings({ ...settings, in_app_enabled: checked })}
              />
            </div>
            
            <div className="flex items-center justify-between">
              <div className="space-y-0.5 flex items-center gap-3">
                <Mail className="w-5 h-5 text-slate-400" />
                <div>
                  <Label>Email Notifications</Label>
                  <p className="text-sm text-slate-500">Send email alerts</p>
                </div>
              </div>
              <Switch
                checked={settings.email_enabled}
                onCheckedChange={(checked) => setSettings({ ...settings, email_enabled: checked })}
              />
            </div>
          </CardContent>
        </Card>

        {/* Scheduler Status */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings2 className="w-5 h-5 text-purple-500" />
              Background Scheduler
            </CardTitle>
            <CardDescription>
              Automatic upload reminder scheduler - synced with Upload Schedules above
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {schedulerStatus ? (
              <>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {schedulerStatus.is_running ? (
                      <CheckCircle className="w-5 h-5 text-emerald-500" />
                    ) : (
                      <AlertTriangle className="w-5 h-5 text-amber-500" />
                    )}
                    <span className="font-medium">
                      Status: {schedulerStatus.is_running ? 'Running' : 'Stopped'}
                    </span>
                  </div>
                  <span className="text-sm text-slate-500">
                    {schedulerStatus.active_schedules_count || 0} active schedules
                  </span>
                </div>
                
                {/* Schedule-Job Mapping - Shows connection between schedules and scheduled jobs */}
                {schedulerStatus.schedule_job_map && schedulerStatus.schedule_job_map.length > 0 && (
                  <div className="space-y-2">
                    <Label>Active Reminder Jobs (from Upload Schedules)</Label>
                    <div className="bg-slate-50 rounded-lg p-3 space-y-3">
                      {schedulerStatus.schedule_job_map.map((item, idx) => (
                        <div key={idx} className="flex justify-between items-center text-sm border-b border-slate-200 pb-2 last:border-0 last:pb-0">
                          <div>
                            <span className="font-medium capitalize">{item.function}</span>
                            <span className="text-slate-400 mx-2">•</span>
                            <span className="capitalize">{item.cadence}</span>
                            <div className="text-xs text-slate-500 mt-0.5">
                              Due: {item.cadence === 'weekly' 
                                ? DAYS_OF_WEEK.find(d => d.value === item.due_day)?.label 
                                : `Day ${item.due_day}`} at {formatTime(item.due_hour, item.due_minute)}
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-slate-600">
                              {item.next_run ? (
                                <>Next: {new Date(item.next_run).toLocaleString()}</>
                              ) : (
                                <span className="text-amber-600">Not scheduled</span>
                              )}
                            </div>
                            <div className="text-xs text-slate-400">{item.trigger}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* System Jobs */}
                {schedulerStatus.jobs && schedulerStatus.jobs.length > 0 && (
                  <div className="space-y-2">
                    <Label>System Jobs</Label>
                    <div className="bg-slate-50 rounded-lg p-3 space-y-2">
                      {schedulerStatus.jobs.filter(job => 
                        job.id === 'check_overdue_uploads' || job.id === 'sync_schedule_jobs'
                      ).map((job) => (
                        <div key={job.id} className="flex justify-between items-center text-sm">
                          <span className="font-medium">{job.id.replace(/_/g, ' ')}</span>
                          <span className="text-slate-500">
                            Next: {job.next_run ? new Date(job.next_run).toLocaleString() : 'N/A'}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {schedulerStatus.recent_reminders && schedulerStatus.recent_reminders.length > 0 && (
                  <div className="space-y-2">
                    <Label>Recent Reminders Sent</Label>
                    <div className="bg-slate-50 rounded-lg p-3 space-y-2">
                      {schedulerStatus.recent_reminders.map((reminder, idx) => (
                        <div key={idx} className="flex justify-between items-center text-sm">
                          <span>{reminder.function}/{reminder.cadence}</span>
                          <span className="text-slate-500">
                            Level {reminder.last_escalation_level} • {reminder.reminder_count} sent
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex gap-3">
                  <Button 
                    variant="outline" 
                    onClick={triggerSchedulerCheck}
                    disabled={triggeringScheduler}
                  >
                    {triggeringScheduler ? (
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Play className="w-4 h-4 mr-2" />
                    )}
                    Trigger Manual Check
                  </Button>
                  <Button 
                    variant="ghost" 
                    onClick={fetchSchedulerStatus}
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Refresh Status
                  </Button>
                </div>
              </>
            ) : (
              <Skeleton className="h-24 w-full" />
            )}
          </CardContent>
        </Card>

        {/* Test Notifications */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Send className="w-5 h-5 text-emerald-500" />
              Test Notifications
            </CardTitle>
            <CardDescription>
              Send test notifications to verify your setup
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Function</Label>
                <Select value={testData.function} onValueChange={(v) => setTestData({ ...testData, function: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="brokerage">Brokerage</SelectItem>
                    <SelectItem value="mga">MGA</SelectItem>
                    <SelectItem value="marketing">Marketing</SelectItem>
                    <SelectItem value="finance">Finance</SelectItem>
                    <SelectItem value="people">People</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Status (for KPI test)</Label>
                <Select value={testData.status} onValueChange={(v) => setTestData({ ...testData, status: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="yellow">Yellow (Warning)</SelectItem>
                    <SelectItem value="red">Red (Critical)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => sendTestNotification('kpi')}>
                <AlertTriangle className="w-4 h-4 mr-2" />
                Test KPI Alert
              </Button>
              <Button variant="outline" onClick={() => sendTestNotification('upload')}>
                <Clock className="w-4 h-4 mr-2" />
                Test Upload Reminder
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Save Button */}
        <div className="flex justify-end">
          <Button 
            onClick={saveSettings} 
            disabled={saving}
            className="bg-[#2563EB]"
          >
            {saving ? 'Saving...' : 'Save Settings'}
          </Button>
        </div>
      </div>
    </Layout>
  );
};

export default NotificationSettings;
